import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { adminLogin, getToken } from "@/lib/api";
import { MarkBeyond } from "@/components/BrandLogos";
import { useDocumentMeta } from "@/lib/meta";

const field =
  "w-full border border-input bg-transparent px-4 py-3 text-foreground placeholder:text-muted-foreground/70 focus:border-accent focus:outline-none transition-colors";

export default function AdminLogin() {
  const navigate = useNavigate();
  useDocumentMeta({ title: "Owner console — Sign in", description: "Beyond the Box owner console.", path: "/admin/login" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (getToken()) navigate("/admin", { replace: true });
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminLogin(email.trim().toLowerCase(), password);
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed. Check your credentials.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background" data-testid="admin-login-page">
      <div className="border-b border-border">
        <div className="mx-auto flex w-full max-w-[1360px] items-center justify-between px-6 py-4 md:px-10">
          <Link to="/" className="flex items-center gap-3 text-foreground">
            <MarkBeyond className="h-7 w-7" strokeWidth={2} />
            <span className="font-display text-xl font-medium tracking-tight">Beyond the Box</span>
          </Link>
          <span className="overline text-muted-foreground">Owner console</span>
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center px-6 py-20">
        <div className="w-full max-w-md">
          <span className="overline text-accent">Restricted</span>
          <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight text-foreground">
            Sign in to the owner console.
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            View and manage walkthrough requests submitted from the site.
          </p>

          <form onSubmit={onSubmit} className="mt-8 border border-border p-7" data-testid="admin-login-form">
            <label className="overline mb-2 block text-muted-foreground">Email</label>
            <input
              data-testid="admin-login-email"
              type="email"
              className={field}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="owner@beyondthebox.law"
              required
            />
            <label className="overline mb-2 mt-6 block text-muted-foreground">Password</label>
            <input
              data-testid="admin-login-password"
              type="password"
              className={field}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={busy}
              className="mt-8 inline-flex w-full items-center justify-center gap-2 bg-primary px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-primary-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
